// Export API service and other services
import api from './api';

// Type for axios error
interface AxiosErrorLike {
  code?: string;
  response?: {
    status?: number;
  };
}

// Type for config with headers
interface ApiConfig {
  headers?: Record<string, string>;
  [key: string]: unknown;
}

// Create enhanced API with 404 error handling for common endpoints
const enhancedApi = {
  ...api,
  // Override the get method to handle common 404 errors silently
  get: async (url: string, config?: ApiConfig) => {
    try {
      // For metrics endpoints, set a flag to prevent auth loops
      const customConfig: ApiConfig = config || {};
      if (url.includes('/metrics')) {
        customConfig.headers = {
          ...(customConfig.headers || {}),
          'X-Skip-Auth': 'true'
        };
      }

      return await api.get(url, customConfig);
    } catch (err: unknown) {
      const error = err as AxiosErrorLike;
      // Handle network errors for certain endpoints
      if (error.code === 'ERR_NETWORK') {
        const isCriticalEndpoint = !url.includes('/metrics') &&
                                 !url.includes('/monthly-metrics') &&
                                 !url.includes('/documents') &&
                                 !url.includes('/financials');

        if (!isCriticalEndpoint) {
          console.log(`Enhanced API: Network error for non-critical endpoint, handling silently: ${url}`);
          return { data: [] };
        }
        // For critical endpoints, rethrow the error
        throw error;
      }

      // Check if it's a 404 error for one of our known problematic endpoints
      if (error.response?.status === 404) {
        // List of endpoints that may not exist yet but shouldn't break the UI
        const knownMissingEndpoints = [
          '/profit-loss',
          '/documents',
          '/monthly-metrics',
          '/metrics',
          '/valuations',
          '/financials',
          '/ai-analysis',
          '/adr-metrics',
          '/revpar-metrics',
          '/occupancy-metrics'
        ];

        // Check if the URL contains any of the known missing endpoints
        const isKnownMissingEndpoint = knownMissingEndpoints.some(endpoint =>
          url.includes(endpoint)
        );

        if (isKnownMissingEndpoint) {
          console.log(`Enhanced API: Endpoint not found (404) but handling silently: ${url}`);
          // Return empty data instead of throwing an error
          return { data: [] };
        }
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        // For metrics endpoints, handle auth errors silently
        if (url.includes('/metrics')) {
          console.log(`Enhanced API: Auth error for metrics endpoint, handling silently: ${url}`);
          return { data: [] };
        }
      }

      // For other errors, propagate as usual
      throw error;
    }
  },

  // Add convenience methods for common operations
  getHotelDocuments: async (hotelId: string) => {
    try {
      // Try both API formats
      try {
        const response = await api.get(`/api/hotels/${hotelId}/documents`);
        return response.data;
      } catch (err: unknown) {
        const error = err as AxiosErrorLike;
        if (error.response?.status === 404) {
          // Try alternative path
          const altResponse = await api.get(`/hotels/${hotelId}/documents`);
          return altResponse.data;
        } else if (error.code === 'ERR_NETWORK') {
          console.log('Network error when fetching hotel documents, returning empty array');
          return [];
        }
        throw error;
      }
    } catch (error) {
      console.error('Error fetching hotel documents:', error);
      return []; // Return empty array on error
    }
  }
};

export { api, enhancedApi };
